import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Inbox, Send, FileEdit, Trash2, Star, AlertOctagon, Layers, Tag, Bot,
  Search, Plus, X, RefreshCw, Reply, ReplyAll, Forward, MoreHorizontal,
  Paperclip, Sparkles, Archive, MailOpen, Mail, Menu
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import MarkdownEditor from "@/components/MarkdownEditor";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Render markdown into an HTML string (server-safe inline rendering for sending).
function mdToHtml(md) {
  if (!md) return "";
  // Use the same renderer used in <ReactMarkdown> to get consistent output by
  // mounting a temporary detached DOM, then reading its innerHTML.
  // To avoid SSR concerns we just use a simple converter for headings, bold,
  // italics, lists, links, code, paragraphs.
  let html = md
    // escape
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Code blocks
  html = html.replace(/```([\s\S]*?)```/g, (_, c) => `<pre style="background:#f3f4f6;padding:10px;border-radius:4px;overflow:auto"><code>${c}</code></pre>`);
  // Inline code
  html = html.replace(/`([^`\n]+)`/g, "<code style=\"background:#f3f4f6;padding:2px 4px;border-radius:3px\">$1</code>");
  // Headings
  html = html.replace(/^### (.*)$/gm, "<h3 style=\"margin:14px 0 6px;font-size:16px\">$1</h3>");
  html = html.replace(/^## (.*)$/gm, "<h2 style=\"margin:18px 0 8px;font-size:18px\">$1</h2>");
  html = html.replace(/^# (.*)$/gm, "<h1 style=\"margin:20px 0 10px;font-size:22px\">$1</h1>");
  // Bold + italic
  html = html.replace(/\*\*([^*\n]+)\*\*/g, "<b>$1</b>");
  html = html.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<i>$2</i>");
  // Blockquote
  html = html.replace(/^&gt; (.*)$/gm, "<blockquote style=\"border-left:3px solid #d1d5db;margin:8px 0;padding:4px 12px;color:#4b5563\">$1</blockquote>");
  // Lists (group consecutive lines)
  html = html.replace(/(^- .*(?:\n- .*)*)/gm, (block) => "<ul style=\"margin:8px 0;padding-left:22px\">" + block.split("\n").map((l) => l.replace(/^- (.*)/, "<li>$1</li>")).join("") + "</ul>");
  html = html.replace(/(^\d+\. .*(?:\n\d+\. .*)*)/gm, (block) => "<ol style=\"margin:8px 0;padding-left:22px\">" + block.split("\n").map((l) => l.replace(/^\d+\. (.*)/, "<li>$1</li>")).join("") + "</ol>");
  // Links + images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, "<img alt=\"$1\" src=\"$2\" style=\"max-width:100%;border-radius:4px\"/>");
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "<a href=\"$2\" style=\"color:#0b3d91;text-decoration:underline\">$1</a>");
  // Paragraphs (any leftover lines)
  html = html.split(/\n{2,}/).map((para) => {
    if (/^<(h\d|ul|ol|blockquote|pre|img|p)/i.test(para.trim())) return para;
    return `<p style="margin:0 0 10px;line-height:1.55">${para.replace(/\n/g, "<br/>")}</p>`;
  }).join("\n");
  return html;
}

/* ============================================================
   Helpers
============================================================ */
const FOLDERS = [
  { key: "inbox", label: "Inbox", icon: Inbox },
  { key: "starred", label: "Starred", icon: Star },
  { key: "sent", label: "Sent", icon: Send },
  { key: "drafts", label: "Drafts", icon: FileEdit },
  { key: "automated", label: "Automated", icon: Bot },
  { key: "spam", label: "Spam", icon: AlertOctagon },
  { key: "trash", label: "Trash", icon: Trash2 },
  { key: "all", label: "All Mail", icon: Layers },
];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString([], sameYear ? { month: "short", day: "numeric" } : { year: "numeric", month: "short", day: "numeric" });
}

function fullDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString();
}

function initials(email) {
  if (!email) return "?";
  const local = email.split("@")[0] || email;
  const parts = local.split(/[._-]/).filter(Boolean);
  return ((parts[0]?.[0] || "?") + (parts[1]?.[0] || "")).toUpperCase();
}

function avatarBg(email) {
  const palette = ["#0b3d91", "#1e40af", "#0e7490", "#7c2d12", "#9d174d", "#4338ca", "#15803d", "#b45309"];
  let h = 0; for (const ch of (email || "x")) h = (h * 31 + ch.charCodeAt(0)) % 1000;
  return palette[h % palette.length];
}

/* ============================================================
   Sub-components
============================================================ */
function LabelChip({ label, onRemove }) {
  if (!label) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium tracking-wide uppercase"
      style={{ background: `${label.color}1a`, color: label.color, border: `1px solid ${label.color}40` }}
      data-testid={`mailbox-chip-${label.id}`}
    >
      {label.name}
      {onRemove && (
        <button onClick={onRemove} className="hover:opacity-70" data-testid={`mailbox-chip-remove-${label.id}`}>
          <X className="w-2.5 h-2.5" />
        </button>
      )}
    </span>
  );
}

function FolderItem({ folder, active, count, onClick }) {
  const Icon = folder.icon;
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-r-full text-sm transition-colors ${
        active ? "bg-blue-50 text-[#0b3d91] font-semibold" : "text-slate-700 hover:bg-slate-100"
      }`}
      data-testid={`mailbox-folder-${folder.key}`}
    >
      <span className="flex items-center gap-3"><Icon className="w-4 h-4" /> {folder.label}</span>
      {count?.total > 0 && (
        <span className={`text-[11px] font-mono ${active ? "text-[#0b3d91]" : "text-slate-500"}`}>
          {count.unread || count.total}
        </span>
      )}
    </button>
  );
}

function LabelManagerModal({ open, onClose, labels, onChanged }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#0b3d91");
  const [saving, setSaving] = useState(false);
  if (!open) return null;

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/mailbox/labels", { name: name.trim(), color });
      toast.success("Label created");
      setName("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create label"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this label? Threads will lose this tag.")) return;
    try {
      await api.delete(`/admin/mailbox/labels/${id}`);
      toast.success("Label deleted");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="mailbox-label-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-[#0b3d91]">Manage Labels</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="mailbox-label-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b bg-slate-50">
          <div className="flex gap-2">
            <input
              className="flex-1 px-3 py-2 rounded-md border border-slate-300 text-sm focus:outline-none focus:border-[#0b3d91]"
              placeholder="New label name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="mailbox-new-label-input"
            />
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-10 h-9 rounded border border-slate-300 cursor-pointer"
              data-testid="mailbox-new-label-color"
            />
            <button
              onClick={create}
              disabled={saving || !name.trim()}
              className="px-3 py-2 bg-[#0b3d91] text-white text-sm rounded-md hover:bg-[#0a3478] disabled:opacity-50"
              data-testid="mailbox-new-label-create"
            >Add</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {labels.map((l) => (
            <div key={l.id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 rounded">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ background: l.color }} />
                <span className="text-sm text-slate-800">{l.name}</span>
                {l.system && <span className="text-[9px] uppercase font-bold tracking-wider text-slate-400">System</span>}
              </div>
              {!l.system && (
                <button onClick={() => remove(l.id)} className="text-slate-400 hover:text-red-500" data-testid={`mailbox-label-delete-${l.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ComposeModal({ open, onClose, defaults, onSent }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && defaults) {
      setTo(defaults.to || "");
      setSubject(defaults.subject || "");
      setBody(defaults.body || "");
      setCc(defaults.cc || "");
      setBcc("");
      setShowCc(!!defaults.cc);
    } else if (open) {
      setTo(""); setCc(""); setBcc(""); setSubject(""); setBody(""); setShowCc(false);
    }
  }, [open, defaults]);

  if (!open) return null;

  const send = async () => {
    const toList = to.split(",").map((s) => s.trim()).filter(Boolean);
    if (!toList.length) { toast.error("At least one recipient required"); return; }
    if (!subject.trim()) { toast.error("Subject required"); return; }
    setSending(true);
    try {
      const ccList = cc.split(",").map((s) => s.trim()).filter(Boolean);
      const bccList = bcc.split(",").map((s) => s.trim()).filter(Boolean);
      // If body already contains HTML tags, treat as-is; otherwise convert markdown → HTML
      const html = /<[a-z][\s\S]*>/i.test(body) ? body : mdToHtml(body);
      const res = await api.post("/admin/mailbox/compose", {
        to: toList,
        cc: ccList,
        bcc: bccList,
        subject: subject.trim(),
        body_html: html,
        in_reply_to: defaults?.in_reply_to || null,
        thread_id: defaults?.thread_id || null,
      });
      toast.success("Email sent");
      onSent?.(res.data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6" data-testid="mailbox-compose-modal">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="px-4 py-3 bg-[#0b3d91] text-white rounded-t-2xl sm:rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <h3 className="font-semibold text-sm tracking-wide">{defaults?.in_reply_to ? "Reply" : "New Message"}</h3>
          </div>
          <button onClick={onClose} className="hover:opacity-80" data-testid="mailbox-compose-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="flex items-center border-b border-slate-200 py-1">
            <label className="text-xs text-slate-500 w-12">To</label>
            <input value={to} onChange={(e) => setTo(e.target.value)} className="flex-1 text-sm outline-none" placeholder="recipient@example.com, ..." data-testid="mailbox-compose-to" />
            <button onClick={() => setShowCc(!showCc)} className="text-xs text-slate-500 hover:text-[#0b3d91] ml-2" data-testid="mailbox-compose-toggle-cc">{showCc ? "Hide" : "Cc/Bcc"}</button>
          </div>
          {showCc && (
            <>
              <div className="flex items-center border-b border-slate-200 py-1">
                <label className="text-xs text-slate-500 w-12">Cc</label>
                <input value={cc} onChange={(e) => setCc(e.target.value)} className="flex-1 text-sm outline-none" data-testid="mailbox-compose-cc" />
              </div>
              <div className="flex items-center border-b border-slate-200 py-1">
                <label className="text-xs text-slate-500 w-12">Bcc</label>
                <input value={bcc} onChange={(e) => setBcc(e.target.value)} className="flex-1 text-sm outline-none" data-testid="mailbox-compose-bcc" />
              </div>
            </>
          )}
          <div className="flex items-center border-b border-slate-200 py-1">
            <label className="text-xs text-slate-500 w-12">Subj</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="flex-1 text-sm outline-none font-medium" placeholder="Subject" data-testid="mailbox-compose-subject" />
          </div>
          <MarkdownEditor
            value={body}
            onChange={setBody}
            rows={12}
            placeholder="Write your message in Markdown — use the toolbar for formatting…"
            testId="mailbox-compose-body"
          />
        </div>
        <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between rounded-b-xl">
          <div className="text-[11px] text-slate-500">From: <span className="font-mono">support@khojbyte.com</span> · Markdown supported</div>
          <button onClick={send} disabled={sending} className="px-5 py-2 bg-[#0b3d91] text-white text-sm rounded-md hover:bg-[#0a3478] disabled:opacity-50 flex items-center gap-2" data-testid="mailbox-compose-send">
            <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBlock({ msg, labels }) {
  const [expanded, setExpanded] = useState(true);
  const isOut = msg.direction === "out";
  const sender = isOut ? msg.from : msg.from;
  const labelMap = useMemo(() => Object.fromEntries((labels || []).map((l) => [l.id, l])), [labels]);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid={`mailbox-message-${msg.id}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 text-left">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: avatarBg(sender) }}>
          {initials(sender)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-slate-900 truncate">{sender}</span>
            {isOut && <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Sent</span>}
            {!isOut && !msg.read && <span className="text-[9px] uppercase tracking-wider font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">New</span>}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            to {(msg.to || []).join(", ")}{msg.cc?.length ? ` · cc ${msg.cc.join(", ")}` : ""}
          </div>
        </div>
        <div className="text-[11px] text-slate-500 shrink-0" title={fullDate(msg.created_at)}>{fmtDate(msg.created_at)}</div>
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          {msg.html ? (
            <div className="prose prose-sm max-w-none text-slate-800" dangerouslySetInnerHTML={{ __html: msg.html }} />
          ) : msg.text ? (
            <pre className="text-sm text-slate-800 whitespace-pre-wrap font-sans">{msg.text}</pre>
          ) : (
            <div className="text-xs text-slate-400 italic">No body content available.</div>
          )}
          {msg.attachments?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {msg.attachments.map((a) => (
                <a
                  key={a.id}
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/mailbox/attachments/${msg.id}/${a.id}`}
                  download={a.filename}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 px-2.5 py-1.5 border rounded text-xs bg-slate-50 hover:bg-slate-100 hover:border-kb-primary text-slate-700 transition-colors"
                  data-testid={`mailbox-attachment-${a.id}`}
                  title="Click to download"
                >
                  <Paperclip className="w-3.5 h-3.5 text-slate-500" />
                  <span className="font-medium">{a.filename}</span>
                  <span className="text-slate-400">({a.content_type})</span>
                </a>
              ))}
            </div>
          )}
          {(msg.labels?.length || 0) > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {msg.labels.map((lid) => <LabelChip key={lid} label={labelMap[lid]} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Main page
============================================================ */
export default function AdminMailboxPage() {
  const [folder, setFolder] = useState("inbox");
  const [labelFilter, setLabelFilter] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [labels, setLabels] = useState([]);
  const [counts, setCounts] = useState({});
  const [threads, setThreads] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeDefaults, setComposeDefaults] = useState(null);
  const [labelMgrOpen, setLabelMgrOpen] = useState(false);
  const [showLabelMenu, setShowLabelMenu] = useState(false);

  const labelMap = useMemo(() => Object.fromEntries(labels.map((l) => [l.id, l])), [labels]);

  const loadLabels = useCallback(async () => {
    try { const r = await api.get("/admin/mailbox/labels"); setLabels(r.data); }
    catch (e) { toast.error("Failed to load labels"); }
  }, []);

  const loadCounts = useCallback(async () => {
    try { const r = await api.get("/admin/mailbox/folders"); setCounts(r.data); }
    catch (e) { /* silent */ }
  }, []);

  const loadThreads = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await api.get("/admin/mailbox/threads", { params: { folder, label: labelFilter || undefined, q: search || undefined } });
      setThreads(r.data.items || []);
    } catch (e) { toast.error("Failed to load threads"); }
    finally { setLoadingList(false); }
  }, [folder, labelFilter, search]);

  const loadThread = useCallback(async (tid) => {
    if (!tid) { setThread(null); setMessages([]); return; }
    setLoadingDetail(true);
    try {
      const r = await api.get(`/admin/mailbox/threads/${tid}`);
      setThread(r.data.thread); setMessages(r.data.messages || []);
      loadCounts();
    } catch (e) { toast.error("Failed to load thread"); }
    finally { setLoadingDetail(false); }
  }, [loadCounts]);

  useEffect(() => { loadLabels(); loadCounts(); }, [loadLabels, loadCounts]);
  useEffect(() => { loadThreads(); setSelectedId(null); setThread(null); setMessages([]); }, [loadThreads]);
  useEffect(() => { loadThread(selectedId); }, [selectedId, loadThread]);

  // Periodic refresh for new incoming emails
  useEffect(() => {
    const id = setInterval(() => { loadCounts(); if (folder === "inbox" && !selectedId) loadThreads(); }, 30000);
    return () => clearInterval(id);
  }, [loadCounts, loadThreads, folder, selectedId]);

  const onSearch = (e) => { e.preventDefault(); setSearch(searchInput.trim()); };

  const setStar = async (tid, value) => {
    await api.post(`/admin/mailbox/threads/${tid}/star`, { value });
    setThreads((ts) => ts.map((t) => t.id === tid ? { ...t, starred: value } : t));
    if (thread?.id === tid) setThread({ ...thread, starred: value });
    loadCounts();
  };
  const setRead = async (tid, value) => {
    await api.post(`/admin/mailbox/threads/${tid}/read`, { value });
    setThreads((ts) => ts.map((t) => t.id === tid ? { ...t, unread_count: value ? 0 : 1 } : t));
    loadCounts();
  };
  const moveTrash = async (tid, value) => {
    await api.post(`/admin/mailbox/threads/${tid}/trash`, { value });
    toast.success(value ? "Moved to Trash" : "Restored");
    if (selectedId === tid) setSelectedId(null);
    loadThreads(); loadCounts();
  };
  const setLabels_ = async (tid, ids) => {
    const r = await api.post(`/admin/mailbox/threads/${tid}/labels`, { label_ids: ids });
    setThread(r.data);
    setThreads((ts) => ts.map((t) => t.id === tid ? r.data : t));
  };

  const reply = (msg) => {
    const cleanSubj = thread?.subject || msg.subject || "";
    const reSubj = cleanSubj.match(/^re:/i) ? cleanSubj : `Re: ${cleanSubj}`;
    const quoted = `\n\n\n— On ${fullDate(msg.created_at)}, ${msg.from} wrote:\n${(msg.text || msg.html || "").replace(/<[^>]+>/g, "").slice(0, 1000)}`;
    setComposeDefaults({
      to: msg.from,
      subject: reSubj,
      body: quoted,
      in_reply_to: msg.message_id,
      thread_id: msg.thread_id,
    });
    setComposeOpen(true);
  };

  const newCompose = () => {
    setComposeDefaults(null);
    setComposeOpen(true);
  };

  const refreshAll = () => { loadCounts(); loadThreads(); if (selectedId) loadThread(selectedId); };

  const injectTest = async () => {
    const from = prompt("From email (test inbound):", "alice@example.com");
    if (!from) return;
    const subject = prompt("Subject:", "Test inbound — UI preview");
    try {
      await api.post("/admin/mailbox/test-inbound", {
        from_email: from,
        to_email: "support@khojbyte.com",
        subject: subject || "Test",
        body_html: `<p>Hi team —</p><p>This is a <b>simulated</b> inbound email injected for UI testing.</p><p>Best,<br/>${from}</p>`,
      });
      toast.success("Test inbound injected");
      refreshAll();
    } catch (e) { toast.error("Failed to inject test"); }
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="h-[calc(100vh-4rem)] flex bg-slate-50 relative" data-testid="admin-mailbox-page">
      {/* Mobile open-sidebar button */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="lg:hidden absolute top-3 left-3 z-30 p-2 bg-white border border-slate-200 rounded-md shadow-sm"
        aria-label="Open mailbox folders"
        data-testid="mailbox-sidebar-open"
      >
        <Menu className="w-4 h-4 text-kb-ink" />
      </button>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setSidebarOpen(false)}
          data-testid="mailbox-sidebar-backdrop"
        />
      )}
      {/* Sidebar */}
      <aside
        className={`bg-white border-r border-slate-200 flex flex-col z-50 transition-transform duration-200
          fixed inset-y-0 left-0 w-72 lg:static lg:w-60
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="mailbox-sidebar"
      >
        <div className="p-4 flex items-center justify-between">
          <button
            onClick={() => { newCompose(); setSidebarOpen(false); }}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0b3d91] hover:bg-[#0a3478] text-white text-sm rounded-full shadow-sm transition-colors font-medium"
            data-testid="mailbox-compose-btn"
          >
            <Plus className="w-4 h-4" /> Compose
          </button>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden ml-2 p-2 text-kb-ink-muted" data-testid="mailbox-sidebar-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto pb-4">
          <div className="pr-2 space-y-0.5">
            {FOLDERS.map((f) => (
              <FolderItem key={f.key} folder={f} active={folder === f.key && !labelFilter} count={counts[f.key]} onClick={() => { setFolder(f.key); setLabelFilter(null); }} />
            ))}
          </div>
          <div className="mt-5 pl-3 pr-2">
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Labels</h4>
              <button onClick={() => setLabelMgrOpen(true)} className="text-slate-400 hover:text-[#0b3d91]" title="Manage labels" data-testid="mailbox-manage-labels">
                <Tag className="w-3 h-3" />
              </button>
            </div>
            <div className="space-y-0.5">
              {labels.map((l) => (
                <button
                  key={l.id}
                  onClick={() => { setLabelFilter(l.id); setFolder("all"); }}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${labelFilter === l.id ? "bg-blue-50 text-[#0b3d91] font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
                  data-testid={`mailbox-label-${l.id}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: l.color }} />
                  <span className="truncate">{l.name}</span>
                </button>
              ))}
            </div>
          </div>
        </nav>
        <div className="p-3 border-t border-slate-100">
          <button onClick={injectTest} className="w-full text-[11px] text-slate-500 hover:text-[#0b3d91] flex items-center justify-center gap-1.5 py-1.5" data-testid="mailbox-inject-test">
            <Sparkles className="w-3 h-3" /> Inject test email
          </button>
        </div>
      </aside>

      {/* Thread list */}
      <section className={`${selectedId ? "hidden md:flex" : "flex"} w-full md:w-[380px] xl:w-[420px] bg-white border-r border-slate-200 flex-col`}>
        <div className="p-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <form onSubmit={onSearch} className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search subject, sender, body…"
              className="w-full pl-9 pr-9 py-2 bg-slate-100 border-none rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/30"
              data-testid="mailbox-search"
            />
            {searchInput && (
              <button type="button" onClick={() => { setSearchInput(""); setSearch(""); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </form>
          <div className="flex items-center justify-between mt-2 px-1">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              {labelFilter ? labelMap[labelFilter]?.name : FOLDERS.find((f) => f.key === folder)?.label} · {threads.length}
            </div>
            <button onClick={refreshAll} className="text-slate-400 hover:text-[#0b3d91]" title="Refresh" data-testid="mailbox-refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="mailbox-thread-list">
          {loadingList ? (
            <div className="p-8 text-center text-xs text-slate-400">Loading…</div>
          ) : threads.length === 0 ? (
            <div className="p-12 text-center">
              <Inbox className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No emails in this folder yet</p>
              <p className="text-xs text-slate-300 mt-1">Inbound emails to <span className="font-mono">@khojbyte.com</span> arrive here.</p>
            </div>
          ) : threads.map((t) => {
            const unread = (t.unread_count || 0) > 0;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors flex gap-3 ${
                  selectedId === t.id ? "bg-blue-50/70" : ""
                } ${unread ? "bg-white" : "bg-slate-50/40"}`}
                data-testid={`mailbox-thread-${t.id}`}
              >
                <button onClick={(e) => { e.stopPropagation(); setStar(t.id, !t.starred); }} className="shrink-0 mt-0.5" data-testid={`mailbox-star-${t.id}`}>
                  <Star className={`w-4 h-4 ${t.starred ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm truncate ${unread ? "font-bold text-slate-900" : "text-slate-700"}`}>
                      {t.last_msg_from || (t.participants || [])[0] || "—"}
                    </span>
                    <span className={`text-[11px] shrink-0 ${unread ? "font-semibold text-[#0b3d91]" : "text-slate-400"}`}>
                      {fmtDate(t.last_msg_at)}
                    </span>
                  </div>
                  <div className={`text-sm truncate mt-0.5 ${unread ? "text-slate-900 font-medium" : "text-slate-600"}`}>
                    {t.subject || "(no subject)"}
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">{t.last_msg_snippet}</div>
                  {(t.labels?.length || 0) > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {t.labels.slice(0, 3).map((lid) => <LabelChip key={lid} label={labelMap[lid]} />)}
                      {t.msg_count > 1 && <span className="text-[10px] text-slate-400 ml-1">· {t.msg_count} msgs</span>}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Detail */}
      <section className={`${selectedId ? "flex" : "hidden md:flex"} flex-1 bg-slate-50 flex-col overflow-hidden`}>
        {!thread ? (
          <div className="flex-1 flex items-center justify-center text-center px-8">
            <div>
              <MailOpen className="w-14 h-14 text-slate-200 mx-auto mb-3" />
              <h3 className="text-slate-500 font-semibold">Select a conversation</h3>
              <p className="text-sm text-slate-400 mt-1">Inbound emails to <span className="font-mono">*@khojbyte.com</span> appear here.</p>
            </div>
          </div>
        ) : (
          <>
            <header className="bg-white border-b border-slate-200 px-5 py-3 flex items-start gap-3">
              <button onClick={() => setSelectedId(null)} className="md:hidden text-slate-500" data-testid="mailbox-back-mobile">
                <X className="w-5 h-5" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-semibold text-slate-900 text-base truncate">{thread.subject || "(no subject)"}</h2>
                  {(thread.labels || []).map((lid) => <LabelChip key={lid} label={labelMap[lid]} />)}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{thread.msg_count} message{thread.msg_count !== 1 ? "s" : ""} · {(thread.participants || []).join(", ")}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0 relative">
                <button onClick={() => setStar(thread.id, !thread.starred)} className="p-2 hover:bg-slate-100 rounded" title="Star" data-testid="mailbox-detail-star">
                  <Star className={`w-4 h-4 ${thread.starred ? "fill-amber-400 text-amber-400" : "text-slate-400"}`} />
                </button>
                <button onClick={() => setRead(thread.id, !!(thread.unread_count === 0))} className="p-2 hover:bg-slate-100 rounded" title="Toggle unread" data-testid="mailbox-detail-read">
                  {thread.unread_count > 0 ? <MailOpen className="w-4 h-4 text-slate-500" /> : <Mail className="w-4 h-4 text-slate-500" />}
                </button>
                <button onClick={() => setShowLabelMenu(!showLabelMenu)} className="p-2 hover:bg-slate-100 rounded relative" title="Labels" data-testid="mailbox-detail-labels">
                  <Tag className="w-4 h-4 text-slate-500" />
                </button>
                {showLabelMenu && (
                  <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 z-30 w-56">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1 px-1">Toggle labels</div>
                    {labels.map((l) => {
                      const active = (thread.labels || []).includes(l.id);
                      return (
                        <button
                          key={l.id}
                          onClick={async () => {
                            const next = active ? (thread.labels || []).filter((x) => x !== l.id) : [...(thread.labels || []), l.id];
                            await setLabels_(thread.id, next);
                          }}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 text-sm"
                          data-testid={`mailbox-toggle-label-${l.id}`}
                        >
                          <input type="checkbox" readOnly checked={active} className="accent-[#0b3d91]" />
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} />
                          <span>{l.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <button onClick={() => moveTrash(thread.id, !thread.deleted_at)} className="p-2 hover:bg-slate-100 rounded" title={thread.deleted_at ? "Restore" : "Trash"} data-testid="mailbox-detail-trash">
                  {thread.deleted_at ? <Archive className="w-4 h-4 text-emerald-600" /> : <Trash2 className="w-4 h-4 text-slate-500" />}
                </button>
              </div>
            </header>
            <div className="flex-1 overflow-y-auto p-5 space-y-3" onClick={() => showLabelMenu && setShowLabelMenu(false)}>
              {loadingDetail ? (
                <div className="text-xs text-slate-400 text-center py-10">Loading…</div>
              ) : messages.map((m) => <MessageBlock key={m.id} msg={m} labels={labels} />)}
              {messages.length > 0 && (
                <div className="flex gap-2 pt-2">
                  <button onClick={() => reply(messages[messages.length - 1])} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:border-[#0b3d91] hover:text-[#0b3d91] rounded-md text-sm" data-testid="mailbox-reply-btn">
                    <Reply className="w-4 h-4" /> Reply
                  </button>
                  <button onClick={() => { const m = messages[messages.length - 1]; reply({ ...m, from: [m.from, ...(m.cc || [])].join(", ") }); }} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:border-[#0b3d91] hover:text-[#0b3d91] rounded-md text-sm" data-testid="mailbox-reply-all-btn">
                    <ReplyAll className="w-4 h-4" /> Reply All
                  </button>
                  <button onClick={() => {
                    const m = messages[messages.length - 1];
                    setComposeDefaults({ to: "", subject: `Fwd: ${(thread.subject || "").replace(/^fwd:\s*/i, "")}`, body: `\n\n---------- Forwarded message ----------\nFrom: ${m.from}\nDate: ${fullDate(m.created_at)}\nSubject: ${m.subject}\nTo: ${(m.to || []).join(", ")}\n\n${(m.text || m.html || "").replace(/<[^>]+>/g, "")}` });
                    setComposeOpen(true);
                  }} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:border-[#0b3d91] hover:text-[#0b3d91] rounded-md text-sm" data-testid="mailbox-forward-btn">
                    <Forward className="w-4 h-4" /> Forward
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      <ComposeModal open={composeOpen} onClose={() => setComposeOpen(false)} defaults={composeDefaults} onSent={() => { refreshAll(); }} />
      <LabelManagerModal open={labelMgrOpen} onClose={() => setLabelMgrOpen(false)} labels={labels} onChanged={loadLabels} />
    </div>
  );
}
